import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef
} from 'react';

import {
  LineChart,
  Line,
  ResponsiveContainer,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { FaSearch, FaSortAlphaDown, FaSortAlphaUp, FaArrowLeft } from 'react-icons/fa';

import { useMsal } from '@azure/msal-react';
import { getAuthConfig, getApiBaseUrl } from '../../services/configService';

import './LeadershipReport.css';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

const LeadershipReport = () => {
  const [hospitals, setHospitals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedHospital, setSelectedHospital] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [sortBy, setSortBy] = useState('total');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedDivision, setSelectedDivision] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState([]);

  const { instance, accounts } = useMsal();
  const authConfig = getAuthConfig();
  const navigate = useNavigate();
  const logPoll = useCallback((type, payload = {}) => {
    const ts = new Date().toISOString();
    console.log(
      `%c[LeadershipPolling][${type}] ${ts}`,
      'color:#16a34a;font-weight:600;',
      payload
    );
  }, []);
  
  const getAccessToken = async () => {
    if (!accounts.length) return null;

    try {
      const res = await instance.acquireTokenSilent({
        account: accounts[0],
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    } catch {
      const res = await instance.acquireTokenPopup({
        scopes: [`api://${authConfig.clientId}/access_as_user`],
      });
      return res.accessToken;
    }
  };
 
  const filteredHospitals = useMemo(() => {
    return hospitals.filter(h => {
      return (
        (!selectedDivision.length || selectedDivision.includes(h.division)) &&
        (!selectedMarket.length || selectedMarket.includes(h.market)) &&
        (!selectedHospital.length || selectedHospital.includes(h.hospital_name)) &&
        (!selectedUser.length || h.staff_performance?.some(s =>
          selectedUser.includes(s.display_name)
        ))
      );
    });
  }, [
    hospitals,
    selectedDivision,
    selectedMarket,
    selectedHospital,
    selectedUser
  ]);

  const hospitalsForDivision = useMemo(() => {
    return hospitals.filter(h => {
      return (
        (!selectedMarket.length || selectedMarket.includes(h.market)) &&
        (!selectedHospital.length || selectedHospital.includes(h.hospital_name)) &&
        (!selectedUser.length || h.staff_performance?.some(s =>
          selectedUser.includes(s.display_name)
        ))
      );
    });
  }, [hospitals, selectedMarket, selectedHospital, selectedUser]);

  const hospitalsForMarket = useMemo(() => {
    return hospitals.filter(h => {
      return (
        (!selectedDivision.length || selectedDivision.includes(h.division)) &&
        (!selectedHospital.length || selectedHospital.includes(h.hospital_name)) &&
        (!selectedUser.length || h.staff_performance?.some(s =>
          selectedUser.includes(s.display_name)
        ))
      );
    });
  }, [hospitals, selectedDivision, selectedHospital, selectedUser]);

  const hospitalsForHospital = useMemo(() => {
    return hospitals.filter(h => {
      return (
        (!selectedDivision.length || selectedDivision.includes(h.division)) &&
        (!selectedMarket.length || selectedMarket.includes(h.market)) &&
        (!selectedUser.length || h.staff_performance?.some(s =>
          selectedUser.includes(s.display_name)
        ))
      );
    });
  }, [hospitals, selectedDivision, selectedMarket, selectedUser]);

  const hospitalsForUser = useMemo(() => {
    return hospitals.filter(h => {
      return (
        (!selectedDivision.length || selectedDivision.includes(h.division)) &&
        (!selectedMarket.length || selectedMarket.includes(h.market)) &&
        (!selectedHospital.length || selectedHospital.includes(h.hospital_name))
      );
    });
  }, [hospitals, selectedDivision, selectedMarket, selectedHospital]); 

  const getHospitalSignature = useCallback((h) => {
    return [
      h.hospital_name,
      h.journal_metrics?.recovered_revenue,
      h.journal_metrics?.journal_count,
      h.visit_metrics?.issues_pct,
      h.visit_metrics?.unrecovered_revenue,
      (h.trend || []).map(t => t.recovered).join(',')
    ].join('|');
  }, []);

  const fetchLeadership = useCallback(
    async (silent = false) => {
      if (!accounts.length) return;
  
      try {
        const token = await getAccessToken();
        if (!token) return;
  
        const res = await fetch(`${getApiBaseUrl()}/leadership-report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
  
        const json = await res.json();
        const next = Array.isArray(json) ? json : [];
  
        setHospitals(prev => {
          const prevMap = new Map(
            prev.map(h => [h.hospital_name, getHospitalSignature(h)])
          );
  
          const nextMap = new Map(
            next.map(h => [h.hospital_name, getHospitalSignature(h)])
          );
  
          let changed = prev.length !== next.length;
  
          if (!changed) {
            for (const [key, value] of nextMap) {
              if (prevMap.get(key) !== value) {
                changed = true;
                break;
              }
            }
          }
  
          if (changed) {
            if (silent) {
              logPoll('CHANGES_DETECTED', {
                prevCount: prev.length,
                nextCount: next.length,
              });
            }
            return next;
          }
  
          if (silent) logPoll('NO_CHANGES');
          return prev;
        });
  
      } catch (err) {
        console.error('[LeadershipPolling] Fetch failed', err);
      } finally {
        setLoading(false);
      }
    },
    [accounts, getHospitalSignature, logPoll]  
  );

  useEffect(() => {
    fetchLeadership(false);
  }, [fetchLeadership]);

  const fetchRef = useRef(fetchLeadership);

  useEffect(() => {
    fetchRef.current = fetchLeadership;
  }, [fetchLeadership]);

  useEffect(() => {
    logPoll('POLL_START', { interval: POLL_INTERVAL_MS });
  
    const id = setInterval(() => {
      logPoll('POLL_TICK');
      fetchRef.current(true);
    }, POLL_INTERVAL_MS);
  
    return () => {
      logPoll('POLL_STOP');
      clearInterval(id);
    };
  }, [logPoll]);  

  const getScoreColor = (score) => {
    if (score <= 65) return 'bad';
    if (score <= 75) return 'mid';
    return 'good';
  };

  const calculateScore = (h) => {

    const safe = (v) =>
      typeof v === 'number' && isFinite(v) ? v : 0;
  
    const clamp01 = (v) =>
      Math.min(Math.max(v, 0), 1);
  
    /* ======================
       AGGREGATED STAFF VALUES
    ====================== */
  
    const recovered = safe(
      (h.staff_performance || []).reduce(
        (sum, s) => sum + (s.recovered_revenue || 0),
        0
      )
    );
  
    const distinctVisits = safe(
      (h.staff_performance || []).reduce(
        (sum, s) => sum + (s.distinct_visits || 0),
        0
      )
    );
  
    const totalPatients = safe(h.visit_metrics?.total_patients);
    const issuesPct = clamp01(safe(h.visit_metrics?.issues_pct));
    const unrecovered = safe(h.visit_metrics?.unrecovered_revenue);
  
    /* ======================
       ✅ RECOVERY (ABSOLUTE PERFORMANCE)
    ====================== */
  
    const recoveryScore = clamp01(Math.sqrt(recovered / 10000));
  
    /* ======================
       ✅ RISK (UNCHANGED)
    ====================== */
  
    const unrecoveredPerPatient =
      totalPatients > 0 ? safe(unrecovered / totalPatients) : 0;
  
    const revenueComponent = clamp01(unrecoveredPerPatient / 300);
  
    const riskRaw =
      issuesPct * 0.6 +
      revenueComponent * 0.4;
  
    const riskScore = clamp01(1 - riskRaw);
  
    /* ======================
       ✅ REACH (COVERAGE ONLY)
    ====================== */
  
    const coverage =
      totalPatients > 0 ? distinctVisits / totalPatients : 0;
  
    const reachScore = clamp01(coverage);
  
    /* ======================
       TOTAL
    ====================== */
  
    const total =
      recoveryScore * 0.5 +
      riskScore * 0.3 +
      reachScore * 0.2;
  
    const toPct = (v) =>
      Math.round(clamp01(safe(v)) * 100);
  
    return {
      total: toPct(total),
      recovery: toPct(recoveryScore),
      risk: toPct(riskScore),
      reach: toPct(reachScore),
    };
  };

  const processed = useMemo(() => {
    return filteredHospitals
      .filter(h => h.hospital_name !== h.revenue_id)
      .map(h => ({ ...h, scores: calculateScore(h) }))
      .filter(h => {
        const q = search.toLowerCase();
    
        const matchesSearch =
          !search ||
          h.top_labels?.some(l =>
            l.label.toLowerCase().includes(q)
          )
          ||
          h.visit_metrics?.top_label_drivers?.some(o =>
            o.label_name.toLowerCase().includes(q)
          );  

        const matchesDivision =
          !selectedDivision.length || selectedDivision.includes(h.division);
      
        const matchesMarket =
          !selectedMarket.length || selectedMarket.includes(h.market);
        

        const matchesHospital =
          !selectedHospital.length || selectedHospital.includes(h.hospital_name);

        const matchesUser =
          !selectedUser.length || h.staff_performance?.some(s =>
            selectedUser.includes(s.display_name)
          );

        return matchesSearch &&
        matchesHospital &&
        matchesUser &&
        matchesDivision &&
        matchesMarket;

      })
      .sort((a, b) => {
        const val = b.scores[sortBy] - a.scores[sortBy];
        return sortDir === 'desc' ? val : -val;
      });
  }, [
      filteredHospitals,
      search,
      selectedHospital,
      selectedUser,
      selectedDivision,
      selectedMarket,
      sortBy,
      sortDir
  ]);

  const staffSummary = useMemo(() => {

    const map = new Map();
  
    filteredHospitals.forEach(h => {
  
      const totalPatients = h.visit_metrics?.total_patients || 0;
      const unrecovered = h.visit_metrics?.unrecovered_revenue || 0;
  
      (h.staff_performance || []).forEach(s => {
  
        const key = s.display_name;
  
        if (!map.has(key)) {
          map.set(key, {
            name: key,
            hospitals: new Set(),
            journals: 0,
            recovered: 0,
            distinct_visits: 0,
            patients: 0,
            unrecovered: 0
          });
        }
  
        const agg = map.get(key);
  
        const weight =
          h.journal_metrics?.journal_count > 0
            ? s.journal_count / h.journal_metrics.journal_count
            : 0;
  
        agg.hospitals.add(h.hospital_name);
  
        agg.journals += s.journal_count || 0;
        agg.recovered += s.recovered_revenue || 0;
        agg.distinct_visits += s.distinct_visits || 0;
  
        agg.patients += totalPatients * weight;
        agg.unrecovered += unrecovered * weight;
  
      });
    });
  
    return Array.from(map.values()).map(s => ({
      ...s,
      hospital_count: s.hospitals.size
    }));
  
  }, [filteredHospitals]);
 

  const getPerformanceColor = (pct) => {
    const clamped = Math.min(Math.max(pct, 0), 1);
  
    const hue = clamped * 120;
  
    return `hsl(${hue}, 70%, 45%)`;
  };
   
  const rankedStaff = useMemo(() => {
    return staffSummary
      .map(s => {
        const recovered = s.recovered || 0;
        const patients = s.distinct_visits || 0;
  
        // ✅ Goals
        const revenuePct = Math.min(Math.max(recovered / 10000, 0), 1);
        const patientPct = Math.min(Math.max(patients / 100, 0), 1);
  
        // ✅ Average score
        const avgPct = (revenuePct + patientPct) / 2;
  
        return {
          ...s,
          recovered,
          patients,
          revenuePct,
          patientPct,
          avgPct
        };
      })
      .sort((a, b) => b.avgPct - a.avgPct)
      .map((s, i) => ({
        ...s,
        rank: i + 1
      }));
  }, [staffSummary]);
   
  if (loading) return <div>Loading...</div>;

  return (
    <div className="leadership-report">

      {/* CONTROLS */}

      <div className="lr-controls">
        <button
          className="back-btn-leadership"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          title="Back"
        >
          <FaArrowLeft />
        </button>

        {/* SEARCH */}
        <div className="subheader-section search">
          <FaSearch />

          <input
            placeholder="Search driver and opportunity items..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <Select
          isMulti
          placeholder="Filter division..."
          options={[...new Set(hospitalsForDivision.map(h => h.division))]
            .map(d => ({ label: d, value: d }))
          }
          value={selectedDivision.map(d => ({ label: d, value: d }))}
          onChange={(selected) =>
            setSelectedDivision(selected ? selected.map(s => s.value) : [])
          }
          className="multi-select"
          classNamePrefix="custom-select"
        />

        <Select
          isMulti
          placeholder="Filter market..."
          options={[...new Set(hospitalsForMarket.map(h => h.market))]
            .map(m => ({ label: m, value: m }))
          }
          value={selectedMarket.map(m => ({ label: m, value: m }))}
          onChange={(selected) =>
            setSelectedMarket(selected ? selected.map(s => s.value) : [])
          }
          className="multi-select"
          classNamePrefix="custom-select"
        />

        {/* HOSPITAL FILTER */}
        <Select
          isMulti
          closeMenuOnSelect={false}
          placeholder="Filter hospitals..." 
          options={[...new Set(hospitalsForHospital.map(h => h.hospital_name))]
            .sort((a,b) => a.localeCompare(b))
            .map(h => ({ label: h, value: h }))}
          value={selectedHospital.map(h => ({ label: h, value: h }))}
          onChange={(selected) =>
            setSelectedHospital(selected ? selected.map(s => s.value) : [])
          }
          className="multi-select"
          classNamePrefix="custom-select"
        />

        {/* USER FILTER */}
        <Select
          isMulti
          closeMenuOnSelect={false}
          placeholder="Filter ffcs..."    
          options={[...new Set(
            hospitalsForUser.flatMap(h =>
              h.staff_performance?.map(s => s.display_name) || []
            )
          )]
            .sort((a,b) => a.localeCompare(b))
            .map(u => ({ label: u, value: u }))}          
          value={selectedUser.map(u => ({ label: u, value: u }))}
          onChange={(selected) =>
            setSelectedUser(selected ? selected.map(s => s.value) : [])
          }
          className="multi-select"
          classNamePrefix="custom-select"
        />

        {/* 
        <div className="subheader-section sort-control">

          <div className="sort-label">Sort</div>

          <div className="sort-select-group">

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="total">Overall</option>
              <option value="recovery">Recovery</option>
              <option value="risk">Risk</option>
              <option value="reach">Reach</option>
            </select>

            <button
              onClick={() =>
                setSortDir(prev => prev === 'desc' ? 'asc' : 'desc')
              }
            >
              {sortDir === 'asc'
                ? <FaSortAlphaDown />
                : <FaSortAlphaUp />
              }
            </button>

          </div>

        </div>
        */}

      </div>

      {/* CARDS */}


      <div className="lr-body">


        <div className="staff-sidebar">
          {rankedStaff.map(s => {

            const avgPercent = Math.round(s.avgPct * 100);
            const revenuePercent = Math.round(s.revenuePct * 100);
            const patientPercent = Math.round(s.patientPct * 100);

            return (
              <div
                key={s.name}
                className="staff-card"
                onClick={() => setSelectedUser([s.name])}
                style={{
                  cursor: "pointer",
                  borderLeft: `6px solid ${getPerformanceColor(s.avgPct)}`,
                  background: s.avgPct === 1 ? "#ecfdf5" : "white"
                }}
              >

                <div className="staff-main">

                  <div className="staff-name">{s.name}</div>

                  <div className="staff-metrics">
                    {s.patients.toLocaleString()} visits ({patientPercent}%) ·{" "}
                    ${Math.round(s.recovered).toLocaleString()} ({revenuePercent}%)
                  </div>

                </div>

                {/* ✅ BIG SCORE */}
                <div
                  className="staff-score-large"
                  style={{ color: getPerformanceColor(s.avgPct) }}
                >
                  {avgPercent}%
                </div>

              </div>
            );
          })}
        </div>
       
        <div className="lr-card-scroll">
          <div className="lr-grid">


            {processed.map(h => {
              const s = h.scores;

              const hospitalParam = encodeURIComponent(h.hospital_name);

              return (           
                <div
                  key={h.revenue_id}
                  className="lr-card"
                  onClick={() => {               
                    navigate(
                      `/visits?page=1&limit=12&sortOrder=asc&hospitalFilter=${hospitalParam}&hideClean=true`,
                      {
                        state: {
                          forceFilters: {
                            hospitalFilter: [h.hospital_name],
                            hideClean: true
                          }
                        }
                      }
                    );
                  }}
                  style={{ cursor: 'pointer' }}
                >

                  <div className="lr-card-v2">

                    {/* HEADER */}
                    <div className="lr-header-dark">

                      <div className="lr-header-left">
                        <div className="lr-hospital">{h.hospital_name}</div>
                        
                          <div className="lr-header-metrics">
                            {h.visit_metrics?.total_patients ?? 0} visits ·{" "}
                            {h.visit_metrics?.issues_count ?? 0} w/issues ·{" "}
                            ${Math.round(h.visit_metrics?.unrecovered_revenue ?? 0).toLocaleString()} unrecovered
                          </div>

                      </div>

                      <div className="lr-trend">
                        <ResponsiveContainer width={120} height={35}>
                          <LineChart data={h.trend}>
                            <Line dataKey="recovered" stroke="#93c5fd" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>

                    </div>
                    {/*
                    <div className="lr-core">

                      <div className={`lr-score-main ${getScoreColor(s.total)}`}>
                        <div className="big">{s.total}</div>
                        <span>Overall</span>
                      </div>

                      <div className="lr-score-side">

                        <div className="score-line tooltip">
                          <span>Recovery</span>

                          <div className="bar">
                            <div
                              className={`fill ${getScoreColor(s.recovery)}`}
                              style={{ width: `${s.recovery}%` }}
                            />
                          </div>

                          <strong>{s.recovery}</strong>

                          <div className="tooltip-box">
                            <div className="tooltip-title">Recovery</div>
                            
                              <div className="tooltip-body">
                                Measures how effectively revenue is captured from total opportunity.
                                <br /><br />
                                Based on:
                                <br />• Recovery rate vs total invoice (75%)
                                <br />• Work effort (journals per visit) (25%)
                                <br /><br />
                                High scores require both strong recovery AND active engagement.
                                <br /><br />
                                Weight: 45% of overall score
                              </div>

                          </div>
                        </div>

                        <div className="score-line tooltip">
                          <span>Risk</span>

                          <div className="bar">
                            <div
                              className={`fill ${getScoreColor(s.risk)}`}
                              style={{ width: `${s.risk}%` }}
                            />
                          </div>

                          <strong>{s.risk}</strong>

                          <div className="tooltip-box">
                            <div className="tooltip-title">Risk</div>
                            
                              <div className="tooltip-body">
                                Measures exposure from missed issues and unrecovered revenue.
                                <br /><br />
                                Based on:
                                <br />• Issue rate (60%)
                                <br />• Unrecovered revenue per patient (40%)
                                <br /><br />
                                Lower exposure results in a higher score.
                                <br /><br />
                                Weight: 30% of overall score
                              </div>

                          </div>
                        </div>

                        <div className="score-line tooltip">
                          <span>Reach</span>

                          <div className="bar">
                            <div
                              className={`fill ${getScoreColor(s.reach)}`}
                              style={{ width: `${s.reach}%` }}
                            />
                          </div>

                          <strong>{s.reach}</strong>

                          <div className="tooltip-box">
                            <div className="tooltip-title">Reach</div>
                            
                              <div className="tooltip-body">
                                Measures how effectively patients are being reviewed and worked.
                                <br /><br />
                                Based on:
                                <br />• Patient coverage (visits reviewed vs total patients) (70%)
                                <br />• Work depth (journals per visit) (30%)
                                <br /><br />
                                High scores require both broad coverage and meaningful activity.
                                <br /><br />
                                Weight: 25% of overall score
                              </div>

                          </div>
                        </div>

                      </div>

                    </div>
                  */}

                    {/* STAFF */}
                    <div className="lr-section">
                      <div className="lr-title-first">FFCs (WTD)</div>

                      {(h.staff_performance || []).length > 0 ? (
                        h.staff_performance.map((s, i) => (
                          <div key={i} className="lr-row">
                            <span className="truncate">{s.display_name}</span>
                            <span className="muted">
                              {s.journal_count.toLocaleString()} · {s.distinct_visits.toLocaleString()} · ${Math.round(s.recovered_revenue || 0).toLocaleString()}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="lr-row empty">No Associate Available</div>
                      )}
                    </div>

                    {/* DRIVERS */}
                    <div className="lr-section">
                      <div className="lr-title">Top Drivers (WTD)</div>

                      {(h.top_labels || []).length > 0 ? (
                        h.top_labels.map((l, i) => (
                          <div key={i} className="lr-row">
                            <span className="truncate">{l.label}</span>
                            <span className="muted">
                              {l.journal_count.toLocaleString()} · ${Math.round(l.recovered_revenue || 0).toLocaleString()}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="lr-row empty">No Recovered Items</div>
                      )}
                    </div>

                    {/* OPPORTUNITIES */}
                    <div className="lr-section">
                      <div className="lr-title">Opportunities</div>

                      {(h.visit_metrics?.top_label_drivers || []).length > 0 ? (
                        h.visit_metrics.top_label_drivers.map((o, i) => (
                          <div key={i} className="lr-row">
                            <span className="truncate">{o.label_name}</span>
                            <span className="muted">
                              {o.count.toLocaleString()} · ${Math.round(o.price_sum || 0).toLocaleString()}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="lr-row empty">No Opportunities</div>
                      )}
                    </div>

                  </div>
                  <div className="lr-spacer" />
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
};

export default LeadershipReport;